import { useCallback, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  limit,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import type { CollectionReference, DocumentData, DocumentReference } from 'firebase/firestore';
import { toast } from '@/hooks/use-toast';
import { db } from '@/lib/firebase';
import {
  buildAppsationsGitHubProxyUrl,
  describeAppsationsGitHubError,
  parseAppsationsGitHubJson,
} from '@/lib/appsationsGitHubProxy';
import {
  encodeGitHubPath,
  mapWithConcurrency,
  parseGitHubRepoIdentity,
  sanitizeCommitForFirestore,
} from '@/pages/developer/githubUtils';
import type { Commit, Expense, JournalEntry, TimeEntry } from '@/pages/developer/types';
import { stripUndefinedFields } from '@/pages/developer/snapshotSanitizer';
import {
  FIRESTORE_COMMITS_PREVIEW_LIMIT,
  GITHUB_COMMITS_PER_PAGE,
} from '@/features/developer/developerConstants';

type GitHubCommitResponse = {
  sha: string;
  html_url?: string;
  commit?: {
    message?: string;
    author?: {
      name?: string;
      date?: string;
    };
  };
  author?: {
    login?: string;
  } | null;
  stats?: {
    additions?: number;
    deletions?: number;
  };
  files?: unknown[];
};

type GitHubSyncParams = {
  repoOwner: string;
  repoName: string;
  ratePerCommit: number;
  commits: Commit[];
  timeEntries: TimeEntry[];
  expenses: Expense[];
  retainerPaid: number;
  journalEntries: JournalEntry[];
  lastCommitSyncAt: string | null;
  firestoreDocRef: DocumentReference<DocumentData> | null;
  firestoreCommitsCollectionRef: CollectionReference<DocumentData> | null;
  loadCommitsFromFirestore: () => Promise<Commit[] | null>;
  setCommits: (commits: Commit[]) => void;
  setDataSource: (source: 'firestore' | 'local' | 'github') => void;
  setLastCommitSyncAt: (value: string | null) => void;
  setFirestoreCommitCount: (value: number | null) => void;
  setFirestoreTotalAdditions: (value: number | null) => void;
  setFirestoreTotalDeletions: (value: number | null) => void;
  setFirestoreTotalStatsCommitCount: (value: number | null) => void;
  setFirestoreTotalsUpdatedAt: (value: string | null) => void;
};

type GitHubSyncReturn = {
  gitHubProxyPingLoading: boolean;
  lastGitHubSyncError: {
    at: string;
    action: 'check-new' | 'reload-all' | 'repo-snapshot' | 'other';
    message: string;
  } | null;
  loadingAllCommits: boolean;
  loadingNewCommits: boolean;
  recordGitHubSyncError: (action: 'check-new' | 'reload-all' | 'repo-snapshot' | 'other', message: string) => void;
  handlePingGitHubProxy: () => Promise<void>;
  handleCheckGitHubAuth: () => Promise<void>;
  fetchAllCommitsFromGitHub: (payload?: { reset?: boolean }) => Promise<void>;
  fetchNewCommitsFromGitHub: () => Promise<void>;
};

export const useDeveloperGitHubSync = (params: GitHubSyncParams): GitHubSyncReturn => {
  const [loadingAllCommits, setLoadingAllCommits] = useState(false);
  const [loadingNewCommits, setLoadingNewCommits] = useState(false);
  const [gitHubProxyPingLoading, setGitHubProxyPingLoading] = useState(false);
  const [lastGitHubSyncError, setLastGitHubSyncError] = useState<GitHubSyncReturn['lastGitHubSyncError']>(null);

  const recordGitHubSyncError = useCallback(
    (action: 'check-new' | 'reload-all' | 'repo-snapshot' | 'other', message: string) => {
      setLastGitHubSyncError({ at: new Date().toISOString(), action, message });
    },
    []
  );

  const handlePingGitHubProxy = useCallback(async () => {
    if (gitHubProxyPingLoading) return;

    const { owner, repo } = parseGitHubRepoIdentity(params.repoOwner, params.repoName);
    if (!owner || !repo) {
      toast({ title: 'Repository required', description: 'Enter the GitHub owner and repository name first.' });
      return;
    }

    setGitHubProxyPingLoading(true);
    try {
      const response = await fetch(buildAppsationsGitHubProxyUrl({ mode: 'ping', owner, repo }));
      if (!response.ok) {
        const detail = await describeAppsationsGitHubError(response);
        toast({
          title: 'GitHub proxy failed',
          description: detail ?? `Proxy ping failed (HTTP ${response.status}).`,
        });
        return;
      }

      const data = await parseAppsationsGitHubJson<{ ok?: boolean }>(response, 'GitHub proxy ping');
      toast({
        title: 'GitHub proxy OK',
        description: data?.ok ? 'Proxy is reachable and returning JSON.' : 'Proxy is reachable.',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to reach GitHub proxy.';
      toast({ title: 'GitHub proxy failed', description: message });
    } finally {
      setGitHubProxyPingLoading(false);
    }
  }, [gitHubProxyPingLoading, params.repoName, params.repoOwner]);

  const handleCheckGitHubAuth = useCallback(async () => {
    const { owner, repo } = parseGitHubRepoIdentity(params.repoOwner, params.repoName);
    if (!owner || !repo) {
      toast({ title: 'Repository required', description: 'Enter the GitHub owner and repository name first.' });
      return;
    }

    try {
      const response = await fetch(buildAppsationsGitHubProxyUrl({ mode: 'whoami', owner, repo }));
      const data = await response.json().catch(() => null as unknown);

      const asObject = (value: unknown): Record<string, unknown> | null => {
        if (value && typeof value === 'object') return value as Record<string, unknown>;
        return null;
      };

      const root = asObject(data);
      const authenticated = root?.authenticated === true;
      const login = typeof root?.login === 'string' ? root.login : null;
      const rateLimit = asObject(root?.rateLimit);
      const remaining = typeof rateLimit?.remaining === 'number' ? rateLimit.remaining : null;
      const resetUnix = typeof rateLimit?.resetUnix === 'number' ? rateLimit.resetUnix : null;
      const resetAt = typeof resetUnix === 'number' && Number.isFinite(resetUnix) ? new Date(resetUnix * 1000).toLocaleString() : null;

      if (authenticated) {
        toast({
          title: `GitHub auth OK${typeof login === 'string' && login ? `: ${login}` : ''}`,
          description:
            typeof remaining === 'number'
              ? `Rate limit: ${remaining} remaining${resetAt ? ` (resets ${resetAt})` : ''}`
              : 'Token is valid and authenticated.',
        });
        return;
      }

      const detail = await describeAppsationsGitHubError(response);
      toast({
        title: 'GitHub auth failed',
        description: detail ?? `Proxy auth check failed (HTTP ${response.status}).`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to reach GitHub proxy.';
      toast({ title: 'Proxy check failed', description: message });
    }
  }, [params.repoName, params.repoOwner]);

  const mapGitHubCommit = useCallback(
    (commit: GitHubCommitResponse): Commit => {
      const author = commit.commit?.author;
      const { owner, repo } = parseGitHubRepoIdentity(params.repoOwner, params.repoName);
      const url =
        typeof commit.html_url === 'string'
          ? commit.html_url
          : owner && repo
            ? `https://github.com/${owner}/${repo}/commit/${commit.sha}`
            : '';

      const next: Commit = {
        sha: commit.sha,
        message: commit.commit?.message ?? 'Untitled commit',
        author: author?.name ?? commit.author?.login ?? 'Unknown',
        date: author?.date ?? new Date().toISOString(),
        url,
        billed: false,
        billingAmount: params.ratePerCommit,
      };

      if (typeof commit.stats?.additions === 'number') {
        next.additions = commit.stats.additions;
      }

      if (typeof commit.stats?.deletions === 'number') {
        next.deletions = commit.stats.deletions;
      }

      if (Array.isArray(commit.files)) {
        next.filesChanged = commit.files.length;
      }

      return next;
    },
    [params.ratePerCommit, params.repoName, params.repoOwner]
  );

  const updateFirestoreCommits = useCallback(
    async (nextCommits: Commit[]) => {
      if (!params.firestoreDocRef) return;

      const commitsCollectionRef = collection(params.firestoreDocRef, 'commits');
      const syncedAt = new Date().toISOString();
      const normalizedRepo = parseGitHubRepoIdentity(params.repoOwner, params.repoName);
      const sanitizedCommits = nextCommits.map((commit) => sanitizeCommitForFirestore(commit, params.ratePerCommit));

      const totals = sanitizedCommits.reduce(
        (acc, commit) => {
          if (typeof commit.additions === 'number') acc.additions += commit.additions;
          if (typeof commit.deletions === 'number') acc.deletions += commit.deletions;
          if (typeof commit.additions === 'number' && typeof commit.deletions === 'number') acc.statsCommitCount += 1;
          return acc;
        },
        { additions: 0, deletions: 0, statsCommitCount: 0 }
      );

      const writeCommitDocuments = async () => {
        if (!db) return;
        let batch = writeBatch(db);
        let ops = 0;

        for (const commit of sanitizedCommits) {
          const commitRef = doc(commitsCollectionRef, commit.sha);
          const committedAt = Timestamp.fromDate(new Date(commit.date));
          batch.set(
            commitRef,
            stripUndefinedFields({
              ...commit,
              committedAt,
              syncedAt,
            }),
            { merge: true }
          );
          ops += 1;
          if (ops >= 450) {
            await batch.commit();
            batch = writeBatch(db);
            ops = 0;
          }
        }

        if (ops > 0) {
          await batch.commit();
        }
      };

      await writeCommitDocuments();

      try {
        await updateDoc(params.firestoreDocRef, {
          commits: sanitizedCommits.slice(0, FIRESTORE_COMMITS_PREVIEW_LIMIT),
          commitCount: sanitizedCommits.length,
          totalAdditions: totals.additions,
          totalDeletions: totals.deletions,
          totalStatsCommitCount: totals.statsCommitCount,
          totalsUpdatedAt: syncedAt,
          commitsUpdatedAt: syncedAt,
          lastCommitSyncAt: syncedAt,
          timeEntries: params.timeEntries,
          expenses: params.expenses,
          retainerPaid: params.retainerPaid,
          ratePerCommit: params.ratePerCommit,
          repoOwner: normalizedRepo.owner,
          repoName: normalizedRepo.repo,
          journalEntries: params.journalEntries,
        });
      } catch {
        await setDoc(
          params.firestoreDocRef,
          {
            commits: sanitizedCommits.slice(0, FIRESTORE_COMMITS_PREVIEW_LIMIT),
            commitCount: sanitizedCommits.length,
            totalAdditions: totals.additions,
            totalDeletions: totals.deletions,
            totalStatsCommitCount: totals.statsCommitCount,
            totalsUpdatedAt: syncedAt,
            commitsUpdatedAt: syncedAt,
            lastCommitSyncAt: syncedAt,
            timeEntries: params.timeEntries,
            expenses: params.expenses,
            retainerPaid: params.retainerPaid,
            ratePerCommit: params.ratePerCommit,
            repoOwner: normalizedRepo.owner,
            repoName: normalizedRepo.repo,
            journalEntries: params.journalEntries,
          },
          { merge: true }
        );
      }

      params.setFirestoreCommitCount(sanitizedCommits.length);
      params.setFirestoreTotalAdditions(totals.additions);
      params.setFirestoreTotalDeletions(totals.deletions);
      params.setFirestoreTotalStatsCommitCount(totals.statsCommitCount);
      params.setFirestoreTotalsUpdatedAt(syncedAt);
    },
    [params]
  );

  const clearFirestoreCommitHistory = useCallback(async () => {
    if (!db || !params.firestoreCommitsCollectionRef) return;
    while (true) {
      const snapshot = await getDocs(query(params.firestoreCommitsCollectionRef, limit(500)));
      if (snapshot.empty) break;
      const batch = writeBatch(db);
      snapshot.docs.forEach((docSnap) => batch.delete(docSnap.ref));
      await batch.commit();
    }
  }, [params.firestoreCommitsCollectionRef]);

  const getGitHubHeaders = useCallback(() => {
    const headers: Record<string, string> = { Accept: 'application/vnd.github+json' };
    return headers;
  }, []);

  const hasGitHubToken = useCallback(() => false, []);

  const fetchGitHubCommitsPage = useCallback(
    async (payload: { ownerPath: string; repoPath: string; page: number; perPage: number }) => {
      if (!hasGitHubToken()) {
        const ownerDecoded = decodeURIComponent(payload.ownerPath);
        const repoDecoded = decodeURIComponent(payload.repoPath);
        return fetch(
          buildAppsationsGitHubProxyUrl({
            mode: 'list',
            owner: ownerDecoded,
            repo: repoDecoded,
            page: payload.page,
            perPage: payload.perPage,
          })
        );
      }

      const response = await fetch(
        `https://api.github.com/repos/${payload.ownerPath}/${payload.repoPath}/commits?per_page=${payload.perPage}&page=${payload.page}`,
        { headers: getGitHubHeaders() }
      );

      if (response.ok) return response;

      return response;
    },
    [getGitHubHeaders, hasGitHubToken]
  );

  const fetchGitHubCommitStats = useCallback(
    async (payload: { ownerPath: string; repoPath: string; sha: string }) => {
      if (!hasGitHubToken()) {
        try {
          const ownerDecoded = decodeURIComponent(payload.ownerPath);
          const repoDecoded = decodeURIComponent(payload.repoPath);
          const proxy = await fetch(
            buildAppsationsGitHubProxyUrl({ mode: 'stats', owner: ownerDecoded, repo: repoDecoded, sha: payload.sha })
          );

          if (proxy.ok) {
            const data = await parseAppsationsGitHubJson<{
              additions: number | null;
              deletions: number | null;
              filesChanged: number | null;
            }>(proxy, 'GitHub stats proxy');
            return {
              additions: typeof data.additions === 'number' ? data.additions : null,
              deletions: typeof data.deletions === 'number' ? data.deletions : null,
              filesChanged: typeof data.filesChanged === 'number' ? data.filesChanged : null,
            };
          }
        } catch {
          // fall back to direct GitHub
        }
      }

      const response = await fetch(
        `https://api.github.com/repos/${payload.ownerPath}/${payload.repoPath}/commits/${payload.sha}`,
        { headers: getGitHubHeaders() }
      );

      if (!response.ok) {
        const remaining = response.headers.get('x-ratelimit-remaining');
        const reset = response.headers.get('x-ratelimit-reset');
        const requestId = response.headers.get('x-github-request-id');

        let detail = '';
        try {
          const data = await response.clone().json();
          if (data?.message) detail = String(data.message);
        } catch {
          // ignore
        }

        const suffix = [
          requestId ? `request ${requestId}` : null,
          remaining ? `remaining ${remaining}` : null,
          reset ? `reset ${reset}` : null,
        ].filter(Boolean).join(' · ');

        const message = detail ? `${detail}${suffix ? ` (${suffix})` : ''}` : `HTTP ${response.status}${suffix ? ` (${suffix})` : ''}`;
        throw new Error(message);
      }

      const data = await parseAppsationsGitHubJson<{
        stats?: { additions?: number; deletions?: number };
        files?: unknown[];
      }>(response, 'GitHub commit stats');

      const additions = typeof data.stats?.additions === 'number' ? data.stats.additions : null;
      const deletions = typeof data.stats?.deletions === 'number' ? data.stats.deletions : null;
      const filesChanged = Array.isArray(data.files) ? data.files.length : null;

      return { additions, deletions, filesChanged };
    },
    [getGitHubHeaders, hasGitHubToken]
  );

  const enrichCommitsWithGitHubStats = useCallback(
    async (payload: { ownerPath: string; repoPath: string; commits: Commit[] }) => {
      const candidates = payload.commits.filter(
        (commit) => typeof commit.additions !== 'number' || typeof commit.deletions !== 'number'
      );

      if (candidates.length === 0) return payload.commits;

      const toFetch = candidates;
      const statsBySha = new Map<string, { additions: number | null; deletions: number | null; filesChanged: number | null }>();

      const results = await mapWithConcurrency(
        toFetch,
        async (commit) => {
          try {
            const stats = await fetchGitHubCommitStats({
              ownerPath: payload.ownerPath,
              repoPath: payload.repoPath,
              sha: commit.sha,
            });
            return { sha: commit.sha, ok: true as const, stats };
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            return { sha: commit.sha, ok: false as const, message };
          }
        },
        { concurrency: 3, pauseMs: 50 }
      );

      const failures = results.filter((result) => !result.ok);
      if (failures.length > 0) {
        const first = failures[0];
        toast({
          title: 'Commit stats partially loaded',
          description: `Some +/- stats could not be fetched (${failures.length}/${results.length}). ${first.message}`,
        });
      }

      results.forEach((result) => {
        if (!result.ok) return;
        statsBySha.set(result.sha, result.stats);
      });

      return payload.commits.map((commit) => {
        const stats = statsBySha.get(commit.sha);
        if (!stats) return commit;
        const next: Commit = { ...commit };
        if (typeof stats.additions === 'number') next.additions = stats.additions;
        if (typeof stats.deletions === 'number') next.deletions = stats.deletions;
        if (typeof stats.filesChanged === 'number') next.filesChanged = stats.filesChanged;
        return next;
      });
    },
    [fetchGitHubCommitStats]
  );

  const fetchAllCommitsFromGitHub = useCallback(
    async ({ reset }: { reset?: boolean } = {}) => {
      const { owner, repo } = parseGitHubRepoIdentity(params.repoOwner, params.repoName);
      if (!owner || !repo) {
        toast({ title: 'Repository required', description: 'Enter the GitHub owner and repository name.' });
        return;
      }

      const ownerPath = encodeGitHubPath(owner);
      const repoPath = encodeGitHubPath(repo);

      setLoadingAllCommits(true);
      try {
        setLastGitHubSyncError(null);
        if (reset && params.firestoreDocRef) {
          await clearFirestoreCommitHistory();
          try {
            await updateDoc(params.firestoreDocRef, {
              commits: [],
              commitCount: 0,
              totalAdditions: 0,
              totalDeletions: 0,
              totalStatsCommitCount: 0,
              totalsUpdatedAt: new Date().toISOString(),
            });
          } catch {
            await setDoc(
              params.firestoreDocRef,
              {
                commits: [],
                commitCount: 0,
                totalAdditions: 0,
                totalDeletions: 0,
                totalStatsCommitCount: 0,
                totalsUpdatedAt: new Date().toISOString(),
              },
              { merge: true }
            );
          }
          params.setCommits([]);
        }
        const collected: Commit[] = [];
        let page = 1;
        let keepLoading = true;

        while (keepLoading) {
          const response = await fetchGitHubCommitsPage({
            ownerPath,
            repoPath,
            perPage: GITHUB_COMMITS_PER_PAGE,
            page,
          });

          if (!response.ok) {
            if (response.status === 404 && !hasGitHubToken()) {
              const message =
                'This repo appears private or inaccessible. Ensure the server /api/github proxy is deployed and ACTIONS_TOKEN has access.';
              recordGitHubSyncError('reload-all', message);
              throw new Error(message);
            }
            const detail = await describeAppsationsGitHubError(response);
            const message = detail ?? `Unable to load commits from GitHub (HTTP ${response.status}).`;
            recordGitHubSyncError('reload-all', message);
            throw new Error(message);
          }

          const data = await parseAppsationsGitHubJson<GitHubCommitResponse[]>(response, 'GitHub commits');
          if (!Array.isArray(data)) {
            throw new Error('Unexpected GitHub response.');
          }

          collected.push(...data.map(mapGitHubCommit));
          keepLoading = data.length === GITHUB_COMMITS_PER_PAGE;
          page += 1;
        }

        params.setCommits(collected);
        params.setDataSource('github');
        params.setLastCommitSyncAt(new Date().toISOString());
        let withStats = collected;
        try {
          withStats = await enrichCommitsWithGitHubStats({ ownerPath, repoPath, commits: collected });
        } catch {
          // Non-fatal
        }

        params.setCommits(withStats);
        await updateFirestoreCommits(withStats);
        toast({ title: 'All commits loaded', description: `${collected.length} commits pulled from GitHub.` });
      } catch (error) {
        let message = error instanceof Error ? error.message : 'Unable to load commits from GitHub.';
        if (error instanceof TypeError) {
          message = `${message} This often means a network failure or a blocked request (CORS/proxy not deployed).`;
        }
        recordGitHubSyncError('reload-all', message);
        toast({ title: 'GitHub fetch failed', description: message });
      } finally {
        setLoadingAllCommits(false);
      }
    },
    [
      clearFirestoreCommitHistory,
      enrichCommitsWithGitHubStats,
      fetchGitHubCommitsPage,
      hasGitHubToken,
      mapGitHubCommit,
      params,
      recordGitHubSyncError,
      updateFirestoreCommits,
    ]
  );

  const fetchNewCommitsFromGitHub = useCallback(async () => {
    if (!params.lastCommitSyncAt) {
      await fetchAllCommitsFromGitHub();
      return;
    }

    const { owner, repo } = parseGitHubRepoIdentity(params.repoOwner, params.repoName);
    if (!owner || !repo) {
      toast({ title: 'Repository required', description: 'Enter the GitHub owner and repository name.' });
      return;
    }

    const ownerPath = encodeGitHubPath(owner);
    const repoPath = encodeGitHubPath(repo);

    setLoadingNewCommits(true);
    try {
      setLastGitHubSyncError(null);
      const effectiveResponse = !hasGitHubToken()
        ? await fetchGitHubCommitsPage({ ownerPath, repoPath, perPage: GITHUB_COMMITS_PER_PAGE, page: 1 })
        : await fetch(
            `https://api.github.com/repos/${ownerPath}/${repoPath}/commits?per_page=${GITHUB_COMMITS_PER_PAGE}&page=1`,
            { headers: getGitHubHeaders() }
          );

      if (!effectiveResponse.ok) {
        if (effectiveResponse.status === 404 && !hasGitHubToken()) {
          const message =
            'This repo appears private or inaccessible. Ensure the server /api/github proxy is deployed and ACTIONS_TOKEN has access.';
          recordGitHubSyncError('check-new', message);
          throw new Error(message);
        }
        const detail = await describeAppsationsGitHubError(effectiveResponse);
        const message = detail ?? `Unable to load commits from GitHub (HTTP ${effectiveResponse.status}).`;
        recordGitHubSyncError('check-new', message);
        throw new Error(message);
      }

      const data = await parseAppsationsGitHubJson<GitHubCommitResponse[]>(effectiveResponse, 'GitHub commits');
      if (!Array.isArray(data)) {
        throw new Error('Unexpected GitHub response.');
      }

      const incoming = data.map(mapGitHubCommit);
      const existing = new Set(params.commits.map((commit) => commit.sha));
      const newCommits = incoming.filter((commit) => !existing.has(commit.sha));

      if (newCommits.length === 0) {
        toast({ title: 'No new commits', description: 'GitHub is already up to date.' });
        return;
      }

      let newCommitsWithStats = newCommits;
      try {
        newCommitsWithStats = await enrichCommitsWithGitHubStats({ ownerPath, repoPath, commits: newCommits });
      } catch {
        // Non-fatal
      }

      const baseCommits = (await params.loadCommitsFromFirestore()) ?? params.commits;
      const newShas = new Set(newCommitsWithStats.map((commit) => commit.sha));
      const baseHasAny = baseCommits.length > 0;
      const merged = [...newCommitsWithStats, ...baseCommits.filter((commit) => !newShas.has(commit.sha))];
      const nextCommits = !baseHasAny && params.commits.length > 0 ? [...newCommitsWithStats, ...params.commits] : merged;

      params.setCommits(nextCommits);
      params.setDataSource('github');
      params.setLastCommitSyncAt(new Date().toISOString());
      await updateFirestoreCommits(nextCommits);
      toast({ title: 'New commits added', description: `Added ${newCommits.length} commits.` });
    } catch (error) {
      let message = error instanceof Error ? error.message : 'Unable to load commits from GitHub.';
      if (error instanceof TypeError) {
        message = `${message} This often means a network failure or a blocked request (CORS/proxy not deployed).`;
      }
      recordGitHubSyncError('check-new', message);
      toast({ title: 'GitHub fetch failed', description: message });
    } finally {
      setLoadingNewCommits(false);
    }
  }, [
    enrichCommitsWithGitHubStats,
    fetchAllCommitsFromGitHub,
    fetchGitHubCommitsPage,
    getGitHubHeaders,
    hasGitHubToken,
    mapGitHubCommit,
    params,
    recordGitHubSyncError,
    updateFirestoreCommits,
  ]);

  return {
    gitHubProxyPingLoading,
    lastGitHubSyncError,
    loadingAllCommits,
    loadingNewCommits,
    recordGitHubSyncError,
    handlePingGitHubProxy,
    handleCheckGitHubAuth,
    fetchAllCommitsFromGitHub,
    fetchNewCommitsFromGitHub,
  };
};
